import * as React from 'react'
import styled, { css } from 'styled-components'
import produce from 'immer'
import { Form, Input, Spin, Tooltip, Button, Tabs, Dropdown, Menu, Radio } from 'antd'
import { isNil, set, cloneDeep, unset, get, isEmpty, pick } from 'lodash'
import { showError, resourceNotFound, ValidationError, showClientNotifications } from 'helpers/errors'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  isReadOnly,
  saveImageFileExtensions,
} from 'helpers/formViews'
import { HARD_SPACE, createResetState } from 'helpers/utils'
import barcodeFormats from 'options/labelTemplates/barcodeFormats'
import unitTypes from 'options/labelTemplates/unitTypes'
import mediaTypes from 'options/labelTemplates/mediaTypes'
import sortOrders from 'options/labelTemplates/sortOrders'
import { createGetLabelTemplateWithImages } from 'helpers/labelTemplates'
import { createPrintHandler, resetPrintHandler } from 'helpers/print'
import { t } from 'helpers/i18n'
import { stopEvent } from 'helpers/events'
import { getStorageItem, setStorageItem } from 'helpers/localStorage'
import FormButtons from 'elements/FormButtons'
import Select, { Option, getOptionProps } from 'elements/Select'
import Icon from 'elements/Icon'
import InputNumber from 'elements/InputNumber'
import Checkbox from 'elements/Checkbox'
import Content from 'containers/LabelTemplates/FormView/Content'
import Label from 'components/PrintLabel/Label'
import Modal from 'elements/Modal'
import Help from 'elements/Help'
import { Row, Col } from 'elements/Grid'

const getStorageKey = () => 'labelTemplates.formView'

const inToCm = (inches) => inches * 2.54
const cmToIn = (centimeters) => centimeters / 2.54

const Preview = styled.div`
  position: relative;
  margin-top: 3px;
  height: 220px;
  overflow: auto;
  padding: 12px;

  svg {
    z-index: -1;
  }
`

const Mask = styled.div`
  overflow: hidden;
`

const Wrap = styled.div`
  ${(props) =>
    props.mediaType === 'Sheet' &&
    css`
      display: flex;
      flex-wrap: wrap;
    `}
`

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.printableRef = React.createRef()
    this.getLabelTemplateWithImages = createGetLabelTemplateWithImages(this)
    this.resetState = createResetState(
      this,
      { copyFromExistingSharedTemplatesOnly: false },
      getStorageItem(getStorageKey(), {})
    )
  }

  async componentDidMount() {
    await this.resetState()
    await this.fetchItem()
  }

  transformSaveItemParams = ({ item, values }) => ({
    lines: produce(item.lines, (draft) => {
      draft.forEach((line) => {
        if (line.id <= 0) {
          unset(line, 'id')
        }

        if (item.id) {
          line.templateId = item.id
        }

        line.fields.forEach((field) => {
          if (field.id <= 0) {
            unset(field, 'id')
          }

          if (line.id > 0) {
            field.lineId = line.id
          } else {
            unset(field, 'lineId')
          }
        })
      })
    }),
  })

  saveState = () => setStorageItem(getStorageKey(), pick(this.state, ['copyFromExistingSharedTemplatesOnly']))

  saveItem = async (itemWithImages) => {
    if (!itemWithImages) {
      throw new Error('item is undefined')
    }

    const item = produce(itemWithImages, (draft) => {
      draft.lines.forEach((line) => line.fields.forEach((field) => unset(field, 'image')))
    })

    const saved = item.id ? await this.props.updateItem(item) : await this.props.createItem(item)

    await this.saveImages(itemWithImages, saved.value.data)

    return saved
  }

  extractImages = (item) =>
    item.lines
      .flatMap((line) =>
        line.fields
          .filter((field) => field.fieldType === 'Image' && field.image)
          .map((field) => ({
            ...field.image,
            imageId: field.imageId,
            lineNumber: line.number,
            fieldPosition: field.position,
          }))
      )
      .filter(Boolean)

  saveImages = async (itemWithImages, savedItem) => {
    try {
      const imagesCurrent = this.extractImages(itemWithImages)
      const imagesOriginal = this.extractImages(this.state.itemOriginal)

      const deleting = imagesOriginal.filter((original) => {
        const current = imagesCurrent.find((one) => one.imageId === original.imageId)

        return !current || current.contents !== original.contents
      })

      if (!isEmpty(deleting)) {
        const response = await this.props.deleteDocumentItems(
          deleting.map((image) => {
            const domainObjectId = image.imageId

            return {
              domainObjectId,
              domainObjectType: 'LabelTemplate',
              documentType: 'ObjectImage',
              documentName: `${domainObjectId}`,
            }
          })
        )

        showClientNotifications({ response })
      }

      const creating = imagesCurrent.filter((current) => {
        const original = imagesOriginal.find((one) => one.imageId === current.imageId)

        return !current.imageId || !original || original.contents !== current.contents
      })

      if (!isEmpty(creating)) {
        const response = await this.props.createDocumentItems(
          creating.map((image) => {
            const domainObjectId =
              image.imageId ??
              savedItem.lines
                .find((line) => line.number === image.lineNumber)
                .fields.find((field) => field.position === image.fieldPosition).imageId

            return {
              domainObjectId,
              domainObjectType: 'LabelTemplate',
              documentType: 'ObjectImage',
              fileName: `${domainObjectId}${get(saveImageFileExtensions, image.fileType)}`,
              fileType: image.fileType,
              contents: image.contents,
            }
          })
        )

        showClientNotifications({ response })
      }
    } catch (error) {
      showError({ error })
    }
  }

  getItemId = () =>
    [
      this.state.item?.id,
      this.props.linkTargetRecord?.labelTemplateId,
      this.props.linkTargetRecord?.templateId,
    ].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({})
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
        this.props.getLabelTemplateTypes(),
      ])

      const item = produce(responses[0].value.data, (draft) => {
        let seed = Date.now() * -1

        draft.lines.forEach((line, lineIndex) => {
          line.id = line.id || seed--

          if (line.templateId === 0) {
            unset(line, 'templateId')
          }

          line.fields.forEach((field, fieldIndex) => {
            field.id = field.id || seed--
            field.lineId = line.id
          })
        })
      })

      const [labelTemplateFields, labelTemplates] = await Promise.all([
        this.props.getLabelTemplateFields({ type: item.labelTemplateType }),
        this.props.getLabelTemplates({
          labelTemplateType: item.labelTemplateType,
          sharedTemplatesOnly: this.state.copyFromExistingSharedTemplatesOnly,
        }),
      ])

      const itemWithImages = await this.getLabelTemplateWithImages(item)

      this.setState(
        {
          item: itemWithImages,
          itemOriginal: cloneDeep(itemWithImages),
          fieldSettings: responses[1].value.data.fieldSettings,
          labelTemplateTypes: get(responses[2], 'value.data.items', []),
          labelTemplateFields: labelTemplateFields.value.data.items,
          labelTemplates: labelTemplates.value.data.items,
        },
        () => {
          if (
            !this.state.item?.id &&
            !this.state.labelTemplateTypes.find(
              (one) => one.labelTemplateType === this.state.item.labelTemplateType
            )
          ) {
            this.setItemValue('labelTemplateType', this.state.labelTemplateTypes[0].labelTemplateType)
          }
        }
      )
    } catch (error) {
      showError({ error })
    }
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      }),
      async () => {
        if (name === 'labelTemplateType') {
          try {
            const [labelTemplateFields, labelTemplates] = await Promise.all([
              this.props.getLabelTemplateFields({ type: value }),
              this.props.getLabelTemplates({
                labelTemplateType: value,
                sharedTemplatesOnly: this.state.copyFromExistingSharedTemplatesOnly,
              }),
            ])

            this.setState(
              produce((draft) => {
                draft.labelTemplateFields = labelTemplateFields.value.data.items

                const fieldNames = draft.labelTemplateFields.map((each) => each.fieldType)

                draft.item.lines.forEach((line) => {
                  line.fields = line.fields.filter((each) => fieldNames.includes(each.fieldType))
                })

                draft.labelTemplates = labelTemplates.value.data.items
              }),
              () => this.props.form.setFieldsValue({ sortBy: 'None' })
            )
          } catch (error) {
            showError({ error })
          }
        }

        if (name === 'unitType') {
          const convert = value === 'Centimeters' ? inToCm : cmToIn

          ;[
            'height',
            'width',
            'topMargin',
            'bottomMargin',
            'leftMargin',
            'rightMargin',
            'verticalGap',
            'horizontalGap',
          ].forEach((field) =>
            this.setState(
              produce((draft) => {
                set(draft.item, field, convert(this.props.form.getFieldValue(field)))
              }),
              () => this.props.form.setFieldsValue({ [field]: this.state.item[field] })
            )
          )

          this.setState(
            produce((draft) => {
              draft.item.lines.forEach((line) => {
                line.height = convert(line.height)

                line.fields.forEach((field) => {
                  ;['width', 'borderWidth', 'topMargin', 'bottomMargin', 'leftMargin', 'rightMargin'].forEach(
                    (each) => {
                      field[each] = convert(field[each])
                    }
                  )
                })
              })
            })
          )
        }
      }
    )

  handleContentChange = (lines) =>
    this.setState(
      produce((draft) => {
        draft.item.lines = lines
      })
    )

  handleActionsMenuClick = ({ key }) => {
    if (key === 'copyFromExistingRecord') {
      this.setState({
        copyFromExistingVisible: true,
        copyFromExistingSelectedId: null,
      })
    }
  }

  handleCopyFromExisting = async () => {
    try {
      this.setState({ copyFromExistingLoading: true })

      const { id, name, description, ...rest } = await this.props
        .getItem(this.state.copyFromExistingSelectedId)
        .then((r) => r.value.data)

      const itemWithImages = await this.getLabelTemplateWithImages(cloneDeep(rest))

      this.setState(
        produce((draft) => {
          Object.assign(draft.item, cloneDeep(itemWithImages))

          let seed = Date.now() * -1

          draft.item.lines.forEach((line, lineIndex) => {
            line.id = seed--

            if (draft.item.id) {
              line.templateId = draft.item.id
            } else {
              unset(line, 'templateId')
            }

            line.fields.forEach((field, fieldIndex) => {
              field.id = seed--
              field.lineId = line.id
              unset(field, 'imageId')
              unset(field, 'image.domainObjectId')
            })
          })

          draft.itemOriginal = cloneDeep(draft.item)

          draft.copyFromExistingVisible = false
          draft.copyFromExistingSelectedId = null
        })
      )
    } catch (error) {
      showError({ error })
    } finally {
      this.setState({ copyFromExistingLoading: false })
    }
  }

  render() {
    const { item, fieldSettings, labelTemplateTypes = [], labelTemplateFields = [] } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(this.state.fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, this.state.fieldSettings)

    const readOnly =
      isReadOnly(this) ||
      isNil(labelTemplateTypes.find((one) => one.labelTemplateType === item.labelTemplateType))

    const unitProps = {
      precision: 2,
      step: 0.01,
      formatter: (value) => `${value} ${item.unitType === 'Centimeters' ? 'cm' : 'in'}`,
      parser: (value) => value.replace(/[cmin\s]/g, ''),
    }

    const unit = item.unitType === 'Centimeters' ? 'cm' : 'in'

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          <Dropdown
            overlay={
              <Menu onClick={this.handleActionsMenuClick}>
                <Menu.Item key="copyFromExistingRecord" disabled={readOnly || Boolean(item.id)}>
                  {t('copyFromLabelTemplateRecord')}
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <Button className="form-items-actions">
              {t('actions')}
              <Icon type="KeyboardArrowDown" />
            </Button>
          </Dropdown>
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Row>
              <Col half>
                <Form.Item label={createLabel('labelTemplateType')}>
                  {createFieldDecorator('labelTemplateType')(
                    <Select
                      onChange={(value) => this.setItemValue('labelTemplateType', value)}
                      placeholder={t('none')}
                      allowClear={false}
                      disabled={Boolean(item.id)}
                    >
                      {labelTemplateTypes.map((each) => (
                        <Option key={each.labelTemplateType} value={each.labelTemplateType}>
                          {t(each.labelTemplateType)}
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
              <Col half>
                <Form.Item label={createLabel('name')}>
                  {createFieldDecorator('name')(<Input readOnly={readOnly} autoFocus />)}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col half>
                <Form.Item label={createLabel('description')}>
                  {createFieldDecorator('description')(<Input readOnly={readOnly} />)}
                </Form.Item>
              </Col>
              <Col half>
                <Form.Item label={createLabel('barcodeFormat')}>
                  {createFieldDecorator('barcodeFormat')(
                    <Select
                      onChange={(value) => this.setItemValue('barcodeFormat', value)}
                      placeholder={t('none')}
                      allowClear={false}
                      readOnly={readOnly}
                    >
                      {Object.entries(barcodeFormats).map(([key, value]) => (
                        <Option key={key} value={key}>
                          {t(value)}
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>{t('preview')}</Col>
              <Col xs={12} className="text-right">
                <Tooltip title={t('print')}>
                  <Button size="small" onClick={() => this.setState({ printPreviewVisible: true })}>
                    <Icon type="Print" />
                  </Button>
                </Tooltip>
              </Col>
            </Row>
            <Preview className="ant-input">
              <Label template={item} highlighted={this.state.highlighted} highlightable />
              <Mask
                style={{
                  height: `calc(${item.height + item.verticalGap}${unit} + 16px)`,
                  width:
                    item.mediaType === 'Sheet'
                      ? `calc(${item.width + item.horizontalGap}${unit} + 16px)`
                      : `${item.width}${unit}`,
                }}
              >
                <Wrap
                  style={{
                    minWidth:
                      item.mediaType === 'Sheet' ? `${(item.width + item.horizontalGap) * 2}${unit}` : 'auto',
                  }}
                  mediaType={item.mediaType}
                >
                  <Label template={item} bordered />
                  <Label template={item} bordered ghosted />
                  <Label template={item} bordered ghosted />
                  <Label template={item} bordered ghosted />
                </Wrap>
              </Mask>
            </Preview>
            <Tabs>
              <Tabs.TabPane key="content" tab={t('content')} forceRender>
                <Content
                  parentRecord={item}
                  labelTemplateFields={labelTemplateFields}
                  lines={item.lines}
                  onChange={this.handleContentChange}
                  readOnly={readOnly}
                  onHighlight={(value) => this.setState({ highlighted: value })}
                />
              </Tabs.TabPane>
              <Tabs.TabPane key="label" tab={t('label')} forceRender>
                <Row>
                  <Col xs={12} sm={6}>
                    <Form.Item label={createLabel('height')}>
                      {createFieldDecorator('height')(
                        <InputNumber
                          onChange={(value) => this.setItemValue('height', value)}
                          readOnly={readOnly}
                          min={0}
                          {...unitProps}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item label={createLabel('width')}>
                      {createFieldDecorator('width')(
                        <InputNumber
                          onChange={(value) => this.setItemValue('width', value)}
                          readOnly={readOnly}
                          min={0}
                          {...unitProps}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={6}>
                    <Form.Item label={createLabel('topMargin')}>
                      {createFieldDecorator('topMargin')(
                        <InputNumber
                          onChange={(value) => this.setItemValue('topMargin', value)}
                          readOnly={readOnly}
                          min={0}
                          {...unitProps}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item label={createLabel('bottomMargin')}>
                      {createFieldDecorator('bottomMargin')(
                        <InputNumber
                          onChange={(value) => this.setItemValue('bottomMargin', value)}
                          readOnly={readOnly}
                          min={0}
                          {...unitProps}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} sm={6}>
                    <Form.Item label={createLabel('leftMargin')}>
                      {createFieldDecorator('leftMargin')(
                        <InputNumber
                          onChange={(value) => this.setItemValue('leftMargin', value)}
                          readOnly={readOnly}
                          min={0}
                          {...unitProps}
                        />
                      )}
                    </Form.Item>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Item label={createLabel('rightMargin')}>
                      {createFieldDecorator('rightMargin')(
                        <InputNumber
                          onChange={(value) => this.setItemValue('rightMargin', value)}
                          readOnly={readOnly}
                          min={0}
                          {...unitProps}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Tabs.TabPane>
              <Tabs.TabPane key="page" tab={t('page')} forceRender>
                <Row>
                  <Col half>
                    <Form.Item label={createLabel('mediaType')}>
                      {createFieldDecorator('mediaType')(
                        <Select
                          onChange={(value) => this.setItemValue('mediaType', value)}
                          allowClear={false}
                          readOnly={readOnly}
                        >
                          {Object.entries(mediaTypes).map(([key, value]) => (
                            <Option key={key} value={key}>
                              {t(value)}
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                    <Row>
                      <Col xs={12}>
                        <Form.Item label={createLabel('horizontalCount')}>
                          {createFieldDecorator('horizontalCount')(
                            <InputNumber readOnly={readOnly} min={1} disabled={item.mediaType === 'Roll'} />
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item label={createLabel('verticalCount')}>
                          {createFieldDecorator('verticalCount')(
                            <InputNumber readOnly={readOnly} min={1} disabled={item.mediaType === 'Roll'} />
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12}>
                        <Form.Item label={createLabel('horizontalGap')}>
                          {createFieldDecorator('horizontalGap')(
                            <InputNumber
                              onChange={(value) => this.setItemValue('horizontalGap', value)}
                              readOnly={readOnly}
                              min={0}
                              {...unitProps}
                              disabled={item.mediaType === 'Roll'}
                            />
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item label={createLabel('verticalGap')}>
                          {createFieldDecorator('verticalGap')(
                            <InputNumber
                              onChange={(value) => this.setItemValue('verticalGap', value)}
                              readOnly={readOnly}
                              min={0}
                              {...unitProps}
                            />
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                  <Col half>
                    <Row>
                      <Col xs={12}>
                        <Form.Item label={t('sortBy')}>
                          {createFieldDecorator('sortBy')(
                            <Select placeholder={t('none')} allowClear={false} readOnly={readOnly}>
                              <Option value="None">{t('none')}</Option>
                              {labelTemplateFields.map((each) => (
                                <Option key={each.fieldType} value={each.fieldType}>
                                  {each.fieldTypeName}
                                </Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item label={HARD_SPACE}>
                          {createFieldDecorator('sortOrder')(
                            <Select allowClear={false} readOnly={readOnly}>
                              {Object.entries(sortOrders).map(([key, value]) => (
                                <Option key={key} value={key}>
                                  {t(value)}
                                </Option>
                              ))}
                            </Select>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12}>
                        <Form.Item label={HARD_SPACE}>
                          {createFieldDecorator('groupDuplicates', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel('groupDuplicates')}</Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={12}>
                        <Form.Item label={HARD_SPACE}>
                          {createFieldDecorator('includeImage', { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly} disabled>
                              {createLabel('includeImage')}
                            </Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item label={HARD_SPACE}>
                      {createFieldDecorator('groupByKeywords', { valuePropName: 'checked' })(
                        <Checkbox readOnly={readOnly} disabled>
                          {createLabel('groupByKeywords')}
                        </Checkbox>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Tabs.TabPane>
              <Tabs.TabPane key="units" tab={t('units')} forceRender>
                <Row>
                  <Col half>
                    <Form.Item label={createLabel('unitType')}>
                      {createFieldDecorator('unitType')(
                        <Select
                          onChange={(value) => this.setItemValue('unitType', value)}
                          placeholder={t('none')}
                          allowClear={false}
                          readOnly={readOnly}
                        >
                          {Object.entries(unitTypes).map(([key, value]) => (
                            <Option key={key} value={key}>
                              {t(value)}
                            </Option>
                          ))}
                        </Select>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Tabs.TabPane>
            </Tabs>
          </div>
          <div className="form-buttons-container">
            <FormButtons
              readOnly={readOnly}
              onCancel={this.props.onCancel}
              onSubmit={this.handleSubmit}
              saveButtonLoading={this.state.saveButtonLoading}
              saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
            />
          </div>
        </Form>
        <Modal
          title={t('selectLabelTemplate')}
          visible={this.state.copyFromExistingVisible}
          okText={t('copy')}
          onOk={this.handleCopyFromExisting}
          okButtonProps={{
            loading: this.state.copyFromExistingLoading,
            disabled: !this.state.copyFromExistingSelectedId,
          }}
          onCancel={() =>
            this.setState({
              copyFromExistingVisible: false,
              copyFromExistingSelectedId: null,
            })
          }
        >
          {this.props.userIsDcribCustomer && (
            <Form.Item className="mb-12">
              {
                <Radio.Group
                  value={this.state.copyFromExistingSharedTemplatesOnly ? 'true' : 'false'}
                  onChange={(e) =>
                    this.setState(
                      {
                        copyFromExistingSharedTemplatesOnly: e.target.value === 'true',
                        copyFromExistingSelectedId: null,
                      },
                      async () => {
                        try {
                          this.saveState()

                          const labelTemplates = await this.props
                            .getLabelTemplates({
                              labelTemplateType: item.labelTemplateType,
                              sharedTemplatesOnly: e.target.value === 'true',
                            })
                            .then((r) => r.value.data.items)

                          this.setState({ labelTemplates })
                        } catch (error) {
                          showError({ error })
                        }
                      }
                    )
                  }
                >
                  <Radio value="false">{t('private')}</Radio>
                  <Radio value="true">{t('shared')}</Radio>
                </Radio.Group>
              }
            </Form.Item>
          )}
          <Form.Item
            colon={false}
            label={
              <span>
                {t('labelTemplate')} <Help title={t('copyFromLabelTemplateRecordInfo')} />
              </span>
            }
          >
            <Select
              value={this.state.copyFromExistingSelectedId ?? undefined}
              onChange={(value) => this.setState({ copyFromExistingSelectedId: value })}
              placeholder={t('select')}
            >
              {(this.state.labelTemplates ?? [])
                .filter((each) => each.id !== item.id)
                .map((each) => (
                  <Option key={each.id} value={each.id}>
                    <span {...getOptionProps(each)}>{each.name}</span>
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Modal>
        <Modal
          title={t('printPreview')}
          okText={t('print')}
          onOk={createPrintHandler(this.printableRef)}
          onCancel={() => this.setState({ printPreviewVisible: false }, resetPrintHandler)}
          width={992}
          visible={this.state.printPreviewVisible}
        >
          <div ref={this.printableRef} className="tofino-print-preview">
            <Label template={item} />
          </div>
        </Modal>
      </>
    )
  }
}

export default Form.create()(FormView)
